import React, { FC, useCallback, useState } from 'react'
import { useField } from 'formik'
import { useTranslations } from 'next-intl'
import { useDebounce } from 'utils/useDebounce'
import { SPECIALTY_FIELD_NAME, SPECIALTY_FIELD_PLACEHOLDER } from '../constants'
import { PositionOption } from '../PositionOption/PositionOption'
import { IFormValue } from '../../../validator/IFormValues'
import { CreatableSelectField } from '../../CreatableSelectField/CreatableSelectField'
import style from './FormSpecialtyField.module.css'
import { FormFieldTypes } from '../../../emun/RegistrationFormFieldTypes'
import { IDictionaryItem } from '../../../interfaces/IDictionaryItem'
import { useSpecialtyFindByName } from '../../../api/query/useSpecialtyFindByName'

const DEFAULT_MIN_SEARCH_LENGTH = 0

const renderItem = (option: IDictionaryItem, inputValue: IDictionaryItem, isActive: boolean) => (
  <PositionOption isActive={isActive} optionName={option.name} inputValue={inputValue.name} />
)

const renderValue = (inputValue: IDictionaryItem) => inputValue.name

type FormSpecialtyFieldFieldProps = {
  validate?: (name: string) => (currentValue: IFormValue) => Promise<string>
}

export const FormSpecialtyField: FC<FormSpecialtyFieldFieldProps> = ({ validate }) => {
  const t = useTranslations()
  const [search, setSearch] = useState('')
  const fieldName = t(SPECIALTY_FIELD_NAME)
  const fieldPlaceholder = t(SPECIALTY_FIELD_PLACEHOLDER)
  const [field] = useField(FormFieldTypes.Specialty)
  const { data } = useSpecialtyFindByName(DEFAULT_MIN_SEARCH_LENGTH, { name: useDebounce(search) })

  const onChange = useCallback((value: string): void => {
    if (value.length >= DEFAULT_MIN_SEARCH_LENGTH) {
      setSearch(value)
    }
  }, [])

  return (
    <CreatableSelectField<IDictionaryItem>
      inputClassName={style.FormSpecialtyField}
      name={field.name}
      label={fieldName}
      options={data}
      onChange={onChange}
      placeholder={fieldPlaceholder}
      renderItem={renderItem}
      renderValue={renderValue}
      minSearchLength={DEFAULT_MIN_SEARCH_LENGTH}
      validate={validate && validate(field.name)}
    />
  )
}

FormSpecialtyField.displayName = 'FormSpecialtyField'
